import React__default, { createContext } from 'react';
import styled from 'styled-components';
import createSlots from '../../utils/create-slots.js';
import Box from '../../components/Box/Box.js';
import { get } from '../../constants.js';
import sx from '../../sx.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const {
  Slots,
  Slot
} = createSlots(['Footer', 'RightPane', 'LeftPane', 'Header']);
const RuleBuilderLayoutContext = /*#__PURE__*/createContext(null);
// --------------------------------------------------------
// Root
const RuleBuilderLayout = ({
  children,
  isSplitPane = false,
  ...props
}) => {
  const leftPaneChild = React__default.Children.toArray(children).find(child => /*#__PURE__*/React__default.isValidElement(child) && child.type === LeftPane ? child : null);
  const rightPaneChild = React__default.Children.toArray(children).find(child => /*#__PURE__*/React__default.isValidElement(child) && child.type === RightPane ? child : null);
  const footerChild = React__default.Children.toArray(children).find(child => /*#__PURE__*/React__default.isValidElement(child) && child.type === Footer ? child : null);
  const headerChild = React__default.Children.toArray(children).find(child => /*#__PURE__*/React__default.isValidElement(child) && child.type === Footer ? child : null);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Slots, {
    context: {
      isSplitPane
    }
  }, slots => {
    return /*#__PURE__*/React__default.createElement(RuleBuilderLayoutContext.Provider, {
      value: {
        isSplitPane
      }
    }, /*#__PURE__*/React__default.createElement(Wrapper, null, headerChild ? /*#__PURE__*/React__default.createElement(Box, {
      sx: {
        display: 'flex',
        width: '100%'
      }
    }, slots.Header) : null, /*#__PURE__*/React__default.createElement(Box, {
      sx: {
        display: 'flex',
        flex: '1 1 100%',
        flexWrap: 'wrap',
        maxWidth: '100%'
      }
    }, leftPaneChild ? slots.LeftPane : null, rightPaneChild ? slots.RightPane : null), footerChild ? /*#__PURE__*/React__default.createElement(Box, {
      sx: {
        display: 'flex',
        width: '100%'
      }
    }, slots.Footer) : null, React__default.Children.toArray(children).filter(child => /*#__PURE__*/React__default.isValidElement(child))));
  }));
};
const Wrapper = styled(Box).withConfig({
  displayName: "RuleBuilderLayout__Wrapper",
  componentId: "sc-i57k4f-0"
})(["display:flex;flex-direction:column;height:100%;margin:0;padding:0;"]);

// --------------------------------------------------------
// LeftPane
/**
 * Left pane for Rule builder layout. Must be rendered inside of a Rule Builder
 */
const LeftPane = ({
  children,
  isSplitPane,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Slot, {
    name: "LeftPane"
  }, ({
    isSplitPane
  }) => {
    return /*#__PURE__*/React__default.createElement(StyledLeftPane, _extends({
      isSplitPane: isSplitPane
    }, props), children);
  });
};
LeftPane.displayName = "LeftPane";
const StyledLeftPane = styled(Box).withConfig({
  displayName: "RuleBuilderLayout__StyledLeftPane",
  componentId: "sc-i57k4f-1"
})(["display:flex;flex-basis:100%;background-color:", ";@media (min-width:", "){flex-basis:", ";}", ""], get('colors.elevation.surface.default.[default]'), get('breakpoints.1'), p => p.isSplitPane ? '50%' : '40%', sx);

// --------------------------------------------------------
// RightPane
/**
 * Right pane for Rule builder layout. Must be rendered inside of a Rule Builder
 */
const RightPane = ({
  children,
  isSplitPane,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Slot, {
    name: "RightPane"
  }, ({
    isSplitPane
  }) => {
    return /*#__PURE__*/React__default.createElement(StyledRightPane, _extends({
      isSplitPane: isSplitPane
    }, props), children);
  });
};
RightPane.displayName = "RightPane";
const StyledRightPane = styled(Box).withConfig({
  displayName: "RuleBuilderLayout__StyledRightPane",
  componentId: "sc-i57k4f-2"
})(["display:flex;flex-basis:100%;background-color:", ";@media (min-width:", "){flex-basis:", ";}", ""], get('colors.elevation.surface.default.[default]'), get('breakpoints.1'), p => p.isSplitPane ? '50%' : '60%', sx);

// --------------------------------------------------------
// Header
/**
 * Header for Rule builder layout
 */
const Header = ({
  children,
  hidden = false,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Slot, {
    name: "Header"
  }, ({
    isSplitPane
  }) => {
    return /*#__PURE__*/React__default.createElement(StyledHeader, props, children);
  });
};
Header.displayName = "Header";
const StyledHeader = styled(Box).withConfig({
  displayName: "RuleBuilderLayout__StyledHeader",
  componentId: "sc-i57k4f-3"
})(["display:flex;background-color:", ";width:100%;@media (min-width:", "){}", ""], get('colors.elevation.surface.default.[default]'), get('breakpoints.2'), sx);

// --------------------------------------------------------
// Footer
/**
 * Footer for a RuleBuilder Layout. Must be rendered inside of a Rule Builder
 */
const Footer = ({
  children,
  hidden = false,
  ...props
}) => {
  return /*#__PURE__*/React__default.createElement(Slot, {
    name: "Footer"
  }, ({
    isSplitPane
  }) => {
    return /*#__PURE__*/React__default.createElement(StyledFooter, props, children);
  });
};
Footer.displayName = "Footer";
const StyledFooter = styled(Box).withConfig({
  displayName: "RuleBuilderLayout__StyledFooter",
  componentId: "sc-i57k4f-4"
})(["display:flex;background-color:", ";width:100%;@media (min-width:", "){}", ""], get('colors.elevation.surface.default.[default]'), get('breakpoints.2'), sx);
RuleBuilderLayout.displayName = 'RuleBuilderLayout';
var RuleBuilderLayout$1 = Object.assign(RuleBuilderLayout, {
  Header,
  LeftPane,
  RightPane,
  Footer
});

export { RuleBuilderLayoutContext, Slot, Slots, RuleBuilderLayout$1 as default };
